import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, BadgeCheck, User, Gift, FileBadge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EstimateSlotCard, GoogleRatingBadge } from "@/components/HeroBits";
import { COPY } from "@/constants/copy";

const HERO_IMAGE = "/hero-craftsman.jpg";

const LINES = ["Every repair.", "Done right.", "Guaranteed."];

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TRUST = [
  { icon: ShieldCheck, value: COPY.trust, label: "every job, no exceptions" },
  { icon: BadgeCheck, value: "Lifetime guarantee", label: "on my workmanship" },
  { icon: User, value: "Just me — Bryan", label: "no subcontractors, ever" },
];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative texture-grid overflow-hidden pt-[72px]">
      <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-7xl pt-8 sm:pt-14 lg:pt-20 pb-16 sm:pb-24 lg:pb-28 grid grid-cols-12 gap-y-8 gap-x-4 md:gap-8 lg:gap-12 items-center">
        <div className="col-span-12 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline-label mb-5 flex items-center gap-2"
            data-testid="hero-overline"
          >
            <ShieldCheck className="h-4 w-4" /> {COPY.trust}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] text-navy"
          >
            {LINES.map((line, i) => (
              <MaskedLine key={line} delay={0.15 + i * 0.14}>
                {i === 2 ? <span className="text-terracotta">{line}</span> : line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl"
            data-testid="hero-subcopy"
          >
            I'm Bryan, a one-man handyman shop in North Kitsap. You book online, I show up, I fix it, install it, or build it.
            An honest estimate before I start, and a lifetime guarantee on my work.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-col items-start gap-3"
          >
            <Button
              data-testid="hero-book-button"
              data-book-cta
              onClick={() => onBook(null)}
              className="bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-7 h-12 text-base hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow,background-color]"
            >
              Book the $50 visit <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-xs text-muted-foreground" data-testid="hero-book-subheading">
              <span className="block">{COPY.credit}</span>
              <span className="block mt-0.5" data-testid="hero-book-rates">{COPY.rates}</span>
              <span className="block mt-0.5">New and existing customers welcome.</span>
            </p>
            <button
              type="button"
              data-testid="hero-work-request-button"
              data-book-cta
              onClick={() => onBook(null, "work")}
              className="mt-1 text-sm font-semibold text-navy underline decoration-dotted underline-offset-4 hover:text-terracotta transition-colors"
            >
              Continue my project →
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            data-testid="hero-referral-blurb"
            className="mt-8 flex items-start gap-2.5 text-sm text-navy/70 max-w-md"
          >
            <Gift className="h-4 w-4 text-terracotta flex-shrink-0 mt-0.5" />
            <span>
              <span className="font-semibold text-navy">Refer a friend — you both get $25.</span>{" "}
              After your first visit you'll get a personal link: your friend saves $25 on their first visit, and you get a $25 credit on your next job.
            </span>
          </motion.p>
        </div>

        <div className="col-span-12 lg:col-span-5 relative mt-8 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 w-full h-full bg-navy" aria-hidden="true" />
            <div className="relative overflow-hidden" data-testid="hero-image-frame">
              <motion.img
                src={HERO_IMAGE}
                alt="Solid Home Services craftsman at work"
                style={{ y: imgY }}
                className="w-full h-[420px] lg:h-[520px] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </div>
            <motion.div
              style={{ y: cardY }}
              className="absolute -bottom-6 -right-3 sm:-right-6 bg-white border border-border shadow-xl px-5 py-4"
              data-testid="hero-availability-card"
            >
              <EstimateSlotCard onBook={onBook} />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="col-span-12 flex flex-wrap gap-x-8 gap-y-4"
          data-testid="hero-stats"
        >
          <GoogleRatingBadge />
          {TRUST.map(({ icon: Icon, value, label }) => (
            <div key={label} className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-terracotta" />
              <div>
                <p className="font-display font-bold text-navy leading-none">{value}</p>
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </div>
            </div>
          ))}
          <a
            href="https://secure.lni.wa.gov/verify/Detail.aspx?UBI=606272681&LIC=SOLIDHS742NQ&SAW="
            target="_blank"
            rel="noreferrer"
            data-testid="hero-license-link"
            className="flex items-center gap-3 group"
          >
            <FileBadge className="h-5 w-5 text-terracotta" />
            <div>
              <p className="font-display font-bold text-navy leading-none group-hover:text-terracotta transition-colors">License #SOLIDHS742NQ</p>
              <p className="text-xs text-muted-foreground mt-1 underline decoration-dotted underline-offset-4">verify with the state</p>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
